import { ActionIcon, Box, Button, Group, Stack, Text, Textarea } from '@mantine/core';
import { IconFile, IconX } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { i18nKeys, useTranslation } from 'locales';
import { useRef, useState } from 'react';
import type { InvoiceFeedback } from 'shared/hooks/use-load-third-party-case';
import { removeAttributes } from 'shared/utils/view';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { MessageList } from './MessageList';

type ConversationPanelProps = {
  invoiceFeedback: NonNullable<InvoiceFeedback>;
};

export function ConversationPanel({ invoiceFeedback }: ConversationPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [messages, setMessages] = useState(invoiceFeedback.conversation);
  const [comment, setComment] = useState('');
  const [file, setFile] = useState<File | undefined>();

  const handleSubmit = () => {
    if (!comment.trim()) return;
    invoiceActions.actionsConversationMessage({
      id: invoiceFeedback.id,
      fileUpload: true,
      data: { body: comment, attachment: file },
      callback: ({ data }: { data: unknown }) => {
        queryClient.invalidateQueries({ queryKey: ['third-party-case'] });
        setMessages(prev => ({
          ...prev,
          messages: [...prev.messages, removeAttributes(data)],
        }));
        setComment('');
        setFile(undefined);
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
    });
  };

  return (
    <Stack>
      <MessageList messages={messages} />

      <Stack gap="sm">
        <Textarea
          placeholder={t(i18nKeys.NEW_MESSAGE)}
          value={comment}
          onChange={e => setComment(e.currentTarget.value)}
          minRows={3}
          autosize
          radius="md"
          styles={{ input: { minHeight: 80 } }}
        />
        <Group justify="space-between" align="center">
          {file ? (
            <Group gap="xs">
              <IconFile size={16} />
              <Text size="sm">{file.name}</Text>
              <ActionIcon
                variant="transparent"
                size="sm"
                c="dimmed"
                onClick={() => {
                  setFile(undefined);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              >
                <IconX size={14} />
              </ActionIcon>
            </Group>
          ) : (
            <Box />
          )}
          <Group gap="sm">
            <Button variant="default" onClick={() => fileInputRef.current?.click()}>
              {t(i18nKeys.ADD_FILE)}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={e => setFile(e.target.files?.[0])}
            />
            <Button onClick={handleSubmit} disabled={!comment.trim()}>
              {t(i18nKeys.ACTIVITIES.SEND_MESSAGE)}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Stack>
  );
}
